import { IPDefaultButton, IPSeparator, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import EntityPicker from 'components/Pickers/EntityPicker';
import { getEntity } from 'globalFunctions';
import {
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
  newIcon,
} from 'globalStyles';
import Entity from 'models/entity';
import Task from 'models/tasks/task';
import { TaskRelation, TaskRelationship } from 'models/tasks/taskRelation';
import Tasks from 'models/tasks/tasks';
import AppContext from 'App/AppContext';
import { getTheme } from '@fluentui/react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetTasks } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { FollowUpTemplateList } from './FollowUpTemplateList';
import { useFollowUpTemplates } from './useFollowUpTemplates';

export interface ITaskFollowUpLinkDialogProps {
  isOpen: boolean;
  task: Task;
  relation: TaskRelation;
  onClose: () => void;
  onCreateEmptyTask: (relation: TaskRelation) => void;
  onCreateFromTemplate: (relation: TaskRelation, template: Task) => void;
  onLinkToExisting: (relation: TaskRelation, targetTask: Entity) => void;
}

export const TaskFollowUpLinkDialog = (props: ITaskFollowUpLinkDialogProps) => {
  const { t } = useTranslation(['task', 'translation']);
  const appContext = useContext(AppContext);
  const theme = getTheme();

  const { templateItems, isLoading: isLoadingTemplates, loadTemplates } = useFollowUpTemplates();
  const [allTasks, setAllTasks] = useState<Entity[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState<boolean>(false);
  const isMountedRef = useRef(true);
  const isLoadingTasksRef = useRef(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadTasks = useCallback(async () => {
    if (isLoadingTasksRef.current) return;

    try {
      isLoadingTasksRef.current = true;
      setIsLoadingTasks(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const tasksData: Tasks = await apiGetTasks(true, undefined, undefined, accessToken, appContext.globalDataCache);
      const linkedTaskIds = new Set(
        props.task.taskRelations
          .filter(
            (relation) =>
              relation.relationship === TaskRelationship.FollowUp &&
              relation.taskIdFrom === props.task.taskId &&
              relation.taskIdTo !== null,
          )
          .map((relation) => relation.taskIdTo as number),
      );
      const entities = tasksData.tasks
        .filter((task) => task.taskId !== props.task.taskId && !linkedTaskIds.has(task.taskId))
        .map((task) => getEntity(task));
      if (!isMountedRef.current) return;
      setAllTasks(entities);
    } catch (err) {
      if (!isMountedRef.current) return;
      appContext.setError(err);
    } finally {
      isLoadingTasksRef.current = false;
      if (isMountedRef.current) {
        setIsLoadingTasks(false);
      }
    }
  }, [appContext, props.task.taskId, props.task.taskRelations]);

  useEffect(() => {
    if (!props.isOpen) return;

    loadTemplates();
    loadTasks();
  }, [props.isOpen, loadTemplates, loadTasks]);

  return (
    <GenericModal
      isOpen={props.isOpen}
      title={t('task:Context.LinkToTask')}
      subTitle={t('task:Context.LinkToTaskSubtitle')}
      onClose={props.onClose}
      closeButtonText={t('translation:General.Button.Cancel')}
      optionalButtons={
        <IPDefaultButton
          iconProps={newIcon}
          text={t('task:FollowUp.CreateEmptyTask')}
          onClick={() => props.onCreateEmptyTask(props.relation)}
        />
      }
      maxHeight="82vh"
      maxWidth={620}
      minWidth={520}
    >
      <IPStack tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        <IPStack.Item>
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPText variant="medium" styles={globalTextStylesBold}>
              {t('task:FollowUp.ChooseTemplate')}
            </IPText>
            <IPStack.Item
              styles={{
                root: {
                  border: `1px solid ${theme.palette.neutralLight}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                },
              }}
            >
              <FollowUpTemplateList
                templateItems={templateItems}
                isLoading={isLoadingTemplates}
                onSelect={(template) => props.onCreateFromTemplate(props.relation, template)}
                maxHeight={220}
              />
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        <IPStack.Item>
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPText variant="medium" styles={globalTextStylesBold}>
              {t('task:Context.SearchExistingTask')}
            </IPText>
            <IPStack.Item
              styles={{
                root: {
                  border: `1px solid ${theme.palette.neutralLight}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                },
              }}
            >
              <EntityPicker
                entities={allTasks}
                addSelectedEntity={(entity) => props.onLinkToExisting(props.relation, entity)}
                isLoading={isLoadingTasks}
                isOnPanel={false}
                showHeader={false}
                compact={true}
              />
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      </IPStack>
    </GenericModal>
  );
};
